import React, { useState, useContext, useEffect } from 'react';
import {
  BankOutlined,
  HomeOutlined,
  StarOutlined,
  EnvironmentOutlined,
  WarningOutlined,
  CameraOutlined,
} from '@ant-design/icons';
import { AuthContext } from '../context/AuthContext';
import axios from 'axios';
import dayjs from "dayjs";

const user_avatar = "https://api.dicebear.com/7.x/thumbs/svg?seed=user";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap');

  * { box-sizing: border-box; margin: 0; padding: 0; }

  body {
    font-family: 'DM Sans', sans-serif;
    background: #f0f2f5;
    min-height: 100vh;
    padding: 32px 16px;
  }

  .log-wrapper {
    max-width: 780px;
    margin: 0 auto;
  }

  .log-header {
    margin-bottom: 32px;
  }

  .log-header h2 {
    font-size: 22px;
    font-weight: 600;
    color: #1a1a2e;
    letter-spacing: -0.3px;
  }

  .log-header p {
    font-size: 13px;
    color: #888;
    margin-top: 4px;
    font-family: 'DM Mono', monospace;
  }

  .timeline {
    position: relative;
    padding-left: 28px;
  }

  .timeline::before {
    content: '';
    position: absolute;
    left: 10px;
    top: 12px;
    bottom: 12px;
    width: 2px;
    background: linear-gradient(to bottom, #0e497b, #0e497b, #0e497b);
    border-radius: 2px;
  }

  .timeline-item {
    position: relative;
    margin-bottom: 20px;
    animation: fadeUp 0.4s ease both;
  }

  .timeline-item:nth-child(1) { animation-delay: 0.05s; }
  .timeline-item:nth-child(2) { animation-delay: 0.15s; }
  .timeline-item:nth-child(3) { animation-delay: 0.25s; }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(12px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .dot {
    position: absolute;
    left: -22px;
    top: 18px;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    border: 2.5px solid white;
    box-shadow: 0 0 0 2px currentColor;
    z-index: 1;
  }

  .dot.check { color: #0e497b; background: #0e497b; }
  .dot.punch { color: #0e497b; background: #0e497b; }

  .card {
    background: white;
    border-radius: 14px;
    padding: 18px 20px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04);
    border: 1px solid #f0f0f0;
    transition: box-shadow 0.2s;
  }

  .card:hover {
    box-shadow: 0 4px 20px rgba(0,0,0,0.09);
  }

  .card-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 14px;
  }

  .location-badge {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: #1a1a2e;
  }

  .location-icon {
    width: 26px;
    height: 26px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .type-tag {
    font-size: 11px;
    font-weight: 500;
    font-family: 'DM Mono', monospace;
    padding: 3px 10px;
    border-radius: 20px;
    letter-spacing: 0.4px;
    text-transform: uppercase;
  }

  .type-tag.check { background: #eef0ff; color: #0e497b; }
  .type-tag.punch { background: #f3eeff; color: #0e497b; }

  .times-row {
    display: flex;
    gap: 12px;
    margin-bottom: 14px;
  }

  .time-block {
    flex: 1;
    background: #ffffff;
    border-radius: 10px;
    padding: 10px 5px;
    border: none;
  }

  .time-label {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    color: #aaa;
    margin-bottom: 4px;
  }

  .time-value {
    font-size: 14px;
    font-weight: 500;
    color: #1a1a2e;
    font-family: 'DM Mono', monospace;
  }

  .time-value.missing {
    color: #f59e0b;
    font-size: 12px;
    display: flex;
    align-items: center;
    gap: 5px;
  }

  .photos-section {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .photos-section-label {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    color: #aaa;
    margin-right: 2px;
  }

  .photo-block {
    position: relative;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid #e8e8e8;
    width: 100px;
    height: 100px;
    flex-shrink: 0;
  }

  .photo-block img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .photo-label {
    position: absolute;
    bottom: 3px;
    left: 3px;
    font-size: 8px;
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    background: rgba(0,0,0,0.5);
    color: white;
    padding: 1px 5px;
    border-radius: 20px;
    line-height: 1.6;
  }

  .no-photo {
    width: 48px;
    height: 48px;
    background: #fafafa;
    border: 1px dashed #ddd;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    color: #bbb;
    font-size: 9px;
    flex-shrink: 0;
  }
`;

function getEntryType(entry) {
  return entry?.PunchIn || entry.PunchOut ? 'punch' : 'check';
}

function getInTime(entry, type) {
  return type === "punch" ? entry?.PunchIn : entry?.CheckIn;
}

function getOutTime(entry, type) {
  return type === "punch" ? entry?.PunchOut : entry?.CheckOut;
}

const locationConfig = {
  "Head Office": { Icon: BankOutlined, bg: "#eef0ff", color: "#0e497b" },
  "Crown Residencies": { Icon: HomeOutlined, bg: "#f3eeff", color: "#0e497b" },
  "Enchanted": { Icon: StarOutlined, bg: "#fff7e6", color: "#0e497b" },
};


export default function ImproperLog({ record, employee }) {
  const [attendanceData, setAttendanceData] = useState([]);
  const { authData, backUrl } = useContext(AuthContext);

  const today = dayjs().format("YYYY-MM-DD");

  console.log("RECORD  :", record)
  console.log("Employee :", employee)

  const fetchData = async () => {
    try {
      const params = new URLSearchParams({ date: record.Date });
      if (employee?.id) params.append('employee_id', employee.id);

      const res = await axios.get(
        `${backUrl}/api/hr/attendance-comparsion/?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${authData?.access}`,
          },
        }
      );
      setAttendanceData(res.data.results || []);
    } catch (error) {
      console.error("Error fetching attendance data:", error);
      setAttendanceData([]);
    }
  };

  useEffect(() => {
    fetchData()
  }, [])

  return (
    <>
      <style>{styles}</style>
      <div className="log-wrapper">
        {/* <div className="log-header">
          <h6>Attendance Log</h6>
          <p>{record.Date}</p>
        </div> */}

        <div className="timeline">
          {attendanceData.map((entry, idx) => {
            console.log("Data :", attendanceData)
            const type = getEntryType(entry?.punch_details[0]);
            const inTime = getInTime(entry?.punch_details[0], type);
            const outTime = getOutTime(entry?.punch_details[0], type);
            const locConf = locationConfig[entry.WorkLocation] || { Icon: EnvironmentOutlined, bg: "#f0f0f0", color: "#888" };
            const { Icon } = locConf;

            return (
              <div className="timeline-item" key={idx}>
                <div className={`dot ${type}`} />
                <div className="card">
                  <div className="card-top">
                    <div className="location-badge">
                      {/* <div className="location-icon" style={{ background: locConf.bg }}>
                                                <Icon style={{ fontSize: 13, color: locConf.color }} />
                                            </div> */}
                      {entry.check_in_work_site && entry.check_out_work_site ? (
                        `${entry.check_in_work_site} - ${entry.check_out_work_site}`
                      ) : entry.check_in_work_site ? (
                        entry.check_in_work_site
                      ) : entry.check_out_work_site ? (
                        entry.check_out_work_site
                      ) : entry.WorkLocation}                    </div>
                    {/* <span className={`type-tag ${type}`}>
                      {type === 'check' ? 'Check-in' : 'Punch'}
                    </span> */}
                  </div>

                  {
                    inTime && (
                      <div className="times-row">
                        <div className="time-block">
                          <div className="time-label">{type === 'check' ? 'Check In' : 'Punch In'}</div>
                          {inTime
                            ? <div className="time-value">{inTime}</div>
                            : <div className="time-value missing">No record</div>
                          }
                        </div>
                        <div className="time-block">
                          <div className="time-label">{type === 'check' ? 'Check Out' : 'Punch Out'}</div>
                          {
                            outTime
                              ? <div className="time-value">{outTime}</div>
                              : <div className="time-value missing">No record</div>
                          }
                        </div>
                      </div>
                    )
                  }

                  {
                    type === 'check' && (
                      <div className="photos-section">
                        <span className="photos-section-label">Photos</span>

                        {entry.check_in_photo_url ? (
                          <div className="photo-block">
                            <img src={entry.check_in_photo_url} alt="Check-in" />
                            <span className="photo-label">In</span>
                          </div>
                        ) : (
                          <div className="no-photo">
                            <CameraOutlined style={{ fontSize: 13 }} />
                            <span>In</span>
                          </div>
                        )}

                        {entry.check_in_photo_url ? (
                          <div className="photo-block">
                            <img src={entry.check_out_photo_url} alt="Check-out" />
                            <span className="photo-label">Out</span>
                          </div>
                        ) : (
                          <div className="no-photo">
                            <CameraOutlined style={{ fontSize: 13 }} />
                            <span>Out</span>
                          </div>
                        )}
                      </div>
                    )
                  }

                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}